import React, { forwardRef, ReactElement, ReactNode, useId, useMemo } from 'react';
import { css } from '@emotion/react';
import {
  dropdown_filter_label_error_style,
  dropdown_filter_label_selected_style,
  dropdown_filter_label_text_css,
  dropdown_filter_shrinkable_css,
  dropdown_filter_value_text_css,
  dropdown_label_base_css,
  getDropdownFilterDividerColor,
  getDropdownFilterLabelSizeStyle,
  getDropdownFilterLabelSpacingBySize,
  getDropdownItemFontStyle,
} from './styles';
import { getInputSizeStyle, input_disabled_style, input_error_style } from '../input/styles';
import { DropdownArrowButton } from './DropdownArrowButton';
import { buildCSSWithLength } from '../../styles';
import { Stack } from '../stack';
import { IconProps } from '../icons/generated';
import { ButtonIcon } from '../button/ButtonIcon';
import { Divider } from '../divider';
import { semantic_colors } from '../../foundation';
import { DropdownLabelProps } from './DropdownLabel';
import {
  DROPDOWN_FILTER_LABEL_GAP,
  DROPDOWN_FILTER_VALUE_DIVIDER_LENGTH,
  DROPDOWN_FILTER_VALUE_GAP,
} from './constants';

export interface DropdownFilterLabelProps extends DropdownLabelProps {
  startIcon?: ReactElement<IconProps>;
  /**
   * 레이블 우측 구분선 뒤에 표시할 선택한 값
   */
  valueText?: ReactNode;
}

export const DropdownFilterLabel = forwardRef<HTMLDivElement, DropdownFilterLabelProps>(
  (
    {
      width,
      disabled,
      expanded = false,
      selected = false,
      status,
      children,
      size = 'medium',
      startIcon,
      valueText,
      ...props
    },
    ref,
  ) => {
    const propsStyle = {
      ...(selected && dropdown_filter_label_selected_style),
      ...(status === 'error' && dropdown_filter_label_error_style),
      ...(disabled && input_disabled_style),
    };

    const currentLabelColor = useMemo(() => {
      return disabled
        ? semantic_colors.content.disabled
        : status === 'error'
        ? semantic_colors.state.negative
        : selected
        ? semantic_colors.accent.primary
        : startIcon?.props.color ?? semantic_colors.content.primary;
    }, [status, selected, startIcon]);
    const labelGap = getDropdownFilterLabelSpacingBySize(size);
    const hasValue = valueText != null;
    const id = useId();
    const labelId = `${id}-label`;
    const valueId = `${id}-value`;
    const startIconNode = startIcon && (
      <ButtonIcon size={startIcon.props.size ?? 16} color={currentLabelColor}>
        {startIcon}
      </ButtonIcon>
    );

    return (
      <div
        role='button'
        aria-labelledby={hasValue ? `${labelId} ${valueId}` : labelId}
        css={[
          dropdown_label_base_css,
          getInputSizeStyle(size),
          getDropdownItemFontStyle(size),
          propsStyle,
          css`
            ${buildCSSWithLength('width', width ?? 'max-content')}
            cursor: ${disabled ? 'not-allowed' : 'pointer'};
            color: ${currentLabelColor};
          `,
          getDropdownFilterLabelSizeStyle(size),
          status === 'error' && input_error_style,
        ]}
        ref={ref}
        {...props}
      >
        <Stack
          direction='row'
          as='div'
          gap={hasValue ? DROPDOWN_FILTER_VALUE_GAP : labelGap}
          align='center'
          css={dropdown_filter_shrinkable_css}
        >
          {!hasValue && startIconNode}
          <Stack
            id={labelId}
            direction='row'
            as='div'
            gap={DROPDOWN_FILTER_LABEL_GAP}
            align='center'
            css={hasValue && dropdown_filter_label_text_css}
          >
            {children}
          </Stack>
          {hasValue && (
            <>
              <Divider
                direction='vertical'
                length={DROPDOWN_FILTER_VALUE_DIVIDER_LENGTH}
                color={getDropdownFilterDividerColor({ disabled, status, selected })}
              />
              <Stack direction='row' as='div' gap={labelGap} align='center' css={dropdown_filter_shrinkable_css}>
                {startIconNode}
                <span id={valueId} css={dropdown_filter_value_text_css}>
                  {valueText}
                </span>
              </Stack>
            </>
          )}
        </Stack>
        <Stack direction='row' gap={labelGap} align='center'>
          <DropdownArrowButton
            expanded={expanded}
            colorProps={selected || status === 'error' || disabled ? currentLabelColor : undefined}
            size={size}
          />
        </Stack>
      </div>
    );
  },
);
